import Button from "@mui/material/Button";
import { FormDialog, selectOptionSchema } from "components/common/FormDialog";
import { FIELD_TYPES, SelectOption } from "components/common/FormDialog/types";
import { Table } from "components/common/Table";
import { TableColumn } from "components/common/Table/types";
import {
  PUBLIC_LB_ADDRESS_WARNING,
  internalLoadBalancerNameSchema,
  internalLoadBalancerRequests,
  isIPv4InCIDR,
  poolerAddress
} from "modules/databases/loadBalancers";
import { databasesActions } from "modules/databases/slices";
import { isDatabaseUpdatingSelector } from "modules/databases/selectors";
import {
  Database,
  DatabaseLoadBalancer,
  UpdateDatabaseParams
} from "modules/databases/types";
import { ipACLSchema } from "modules/databases/validation";
import { subnetsSelector } from "modules/networks/selectors";
import { networksActions } from "modules/networks/slices";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { splitMultilineCommaSeparatedList } from "utils/splitMultilineCommaSeparatedList";
import { string } from "yup";
import { REGEX } from "../../constants";
import * as s from "./styles";

type Row = DatabaseLoadBalancer & {
  id: string;
  address: string;
  state: string;
  allowed: string;
  poolerAddress: string;
};
const columns: TableColumn<Row>[] = [
  { key: "name", label: "Name" },
  { key: "subnet_id", label: "Subnet" },
  { key: "address", label: "Address" },
  { key: "allowed", label: "Allowed CIDRs" },
  { key: "state", label: "Status" }
];
const poolerColumn: TableColumn<Row> = {
  key: "poolerAddress",
  label: "Pooler"
};

export const LoadBalancers: FC<{ database: Database }> = ({ database }) => {
  const dispatch = useDispatch();
  const { organizationId, regionId, projectId, serviceName, databaseId } =
    useParams();
  const subnets = useSelector(subnetsSelector);
  const updating = useSelector(isDatabaseUpdatingSelector);
  const [dialog, setDialog] = useState<
    "add" | "edit" | "remove" | "public" | "public-acl" | null
  >(null);
  const [selectedName, setSelectedName] = useState("");
  const selected = database.internal_load_balancers?.find(
    (entry) => entry.name === selectedName
  );
  const publicEnabled =
    database.public_load_balancer_enabled ?? !database.subnet_id;
  const entries = internalLoadBalancerRequests(database);
  const supported = database.public_load_balancer_enabled !== undefined;

  useEffect(() => {
    if (regionId && projectId)
      dispatch(networksActions.getSubnetsRequest({ regionId, projectId }));
  }, [dispatch, regionId, projectId]);

  const update = (data: UpdateDatabaseParams["data"]) => {
    dispatch(
      databasesActions.updateDatabaseRequest({
        orgId: organizationId!,
        projId: projectId!,
        serviceName: serviceName!,
        id: databaseId!,
        data
      })
    );
    setDialog(null);
  };
  const rows: Row[] = (database.internal_load_balancers ?? []).map((entry) => ({
    ...entry,
    id: entry.name,
    address: entry.host ? `${entry.host}:${entry.port}` : "Pending",
    state: entry.ready ? "Ready" : entry.message || "Provisioning",
    allowed: entry.ip_acl?.join(", ") || "All sources",
    poolerAddress: poolerAddress(entry) ?? ""
  }));
  const publicPooler = poolerAddress(database.public_load_balancer);

  return (
    <>
      <s.InfoContainer variant="outlined">
        <s.SectionTitle>Public load balancer</s.SectionTitle>
        <p>
          {publicEnabled
            ? database.public_load_balancer?.host
              ? `${database.public_load_balancer.host}:${database.public_load_balancer.port}`
              : database.public_load_balancer?.message || "Provisioning"
            : database.public_load_balancer?.message || "Disabled"}
        </p>
        {publicEnabled && (
          <p>Allowed CIDRs: {database.ip_acl?.join(", ") || "All sources"}</p>
        )}
        {publicEnabled && publicPooler && <p>Pooler: {publicPooler}</p>}
        <s.CardActions>
          {publicEnabled && (
            <Button
              variant="outlined"
              disabled={updating}
              onClick={() => setDialog("public-acl")}
            >
              Edit allowed CIDRs
            </Button>
          )}
          <Button
            variant={publicEnabled ? "text" : "outlined"}
            color={publicEnabled ? "error" : "primary"}
            disabled={updating || !supported}
            onClick={() => setDialog("public")}
          >
            {publicEnabled
              ? "Disable public load balancer"
              : "Enable public load balancer"}
          </Button>
        </s.CardActions>
      </s.InfoContainer>
      <Table<Row>
        title="Internal load balancers"
        rows={rows}
        columns={
          database.pooler?.enabled ? [...columns, poolerColumn] : columns
        }
        toolbarItems={
          <Button
            variant="contained"
            disabled={updating || !supported || entries.length >= 16}
            onClick={() => {
              setSelectedName("");
              setDialog("add");
            }}
          >
            Add internal load balancer
          </Button>
        }
        actions={[
          {
            label: "Edit",
            handler: (id) => {
              setSelectedName(id);
              setDialog("edit");
            },
            isDisabled: () => updating
          },
          {
            label: "Remove",
            handler: (id) => {
              setSelectedName(id);
              setDialog("remove");
            },
            isDisabled: () => updating,
            isDanger: true
          }
        ]}
      />
      <FormDialog
        isOpened={dialog === "public"}
        onCancel={() => setDialog(null)}
        title={`${publicEnabled ? "Disable" : "Enable"} public load balancer?`}
        confirmButtonLabel={publicEnabled ? "Disable" : "Enable"}
        fields={[
          {
            name: "warning",
            label: PUBLIC_LB_ADDRESS_WARNING,
            type: FIELD_TYPES.NOTES
          }
        ]}
        onConfirm={() =>
          update({ public_load_balancer_enabled: !publicEnabled })
        }
      />
      <FormDialog
        isOpened={dialog === "public-acl"}
        onCancel={() => setDialog(null)}
        title="Edit public load balancer allowed CIDRs"
        confirmButtonLabel="Save"
        fields={[
          {
            name: "ip_acl",
            label: "Allowed CIDRs",
            type: FIELD_TYPES.MULTILINE_TEXT,
            defaultValue: database.ip_acl?.join("\n") || "",
            helperText:
              "One IPv4 CIDR per line. Leave empty to allow all sources on the internet.",
            rules: ipACLSchema
          }
        ]}
        onConfirm={(data: { ip_acl?: string }) =>
          update({
            ip_acl: splitMultilineCommaSeparatedList(data.ip_acl || "")
          })
        }
      />
      <FormDialog
        isOpened={dialog === "remove"}
        onCancel={() => setDialog(null)}
        title={`Remove internal load balancer "${selectedName}"?`}
        confirmButtonLabel="Remove"
        fields={[
          {
            name: "warning",
            label:
              "This releases the endpoint's IP address and disconnects its clients. The database keeps running.",
            type: FIELD_TYPES.NOTES
          }
        ]}
        onConfirm={() =>
          update({
            internal_load_balancers: entries.filter(
              (entry) => entry.name !== selectedName
            )
          })
        }
      />
      <FormDialog
        isOpened={dialog === "add" || dialog === "edit"}
        onCancel={() => setDialog(null)}
        title={
          dialog === "edit"
            ? "Edit internal load balancer"
            : "Add internal load balancer"
        }
        confirmButtonLabel={dialog === "edit" ? "Save" : "Add"}
        fields={[
          {
            name: "name",
            label: "Name",
            type: FIELD_TYPES.TEXT,
            defaultValue: selected?.name || "",
            readOnly: dialog === "edit",
            rules: internalLoadBalancerNameSchema.test(
              "unique",
              "Choose a unique name; default is reserved.",
              (value) =>
                dialog === "edit"
                  ? value === selectedName
                  : value !== "default" &&
                    !entries.some((entry) => entry.name === value)
            )
          },
          {
            name: "subnet",
            label: "Subnet",
            type: FIELD_TYPES.SELECT,
            options: subnets?.map((subnet) => ({
              label: `${subnet.name} (${subnet.cidr})`,
              value: subnet.id
            })),
            defaultValue: selected
              ? {
                  label:
                    subnets?.find((subnet) => subnet.id === selected.subnet_id)
                      ?.name || selected.subnet_id,
                  value: selected.subnet_id
                }
              : null,
            rules: selectOptionSchema
          },
          {
            name: "private_static_ip",
            label: "Private static IP (optional)",
            type: FIELD_TYPES.TEXT,
            defaultValue: selected?.private_static_ip || "",
            rules: string()
              .matches(REGEX.IP_ADDRESS, {
                message: "Enter a valid IPv4 address.",
                excludeEmptyString: true
              })
              .test(
                "subnetRange",
                "IP must be in the selected subnet.",
                (value, context) => {
                  if (!value) return true;
                  const { subnet } = context.parent as {
                    subnet?: SelectOption;
                  };
                  const cidr = subnets?.find(
                    (entry) => entry.id === subnet?.value
                  )?.cidr;
                  return !cidr || isIPv4InCIDR(value, cidr);
                }
              )
          },
          {
            name: "ip_acl",
            label: "Allowed CIDRs",
            type: FIELD_TYPES.MULTILINE_TEXT,
            defaultValue: selected?.ip_acl?.join("\n") || "",
            helperText:
              "One IPv4 CIDR per line. Leave empty to allow all sources on the private network.",
            rules: ipACLSchema
          },
          {
            name: "warning",
            label:
              "Changing the subnet or private IP recreates this endpoint and disconnects its clients. Its assigned IP can change.",
            type: FIELD_TYPES.NOTES
          }
        ]}
        onConfirm={(data: {
          name: string;
          subnet: SelectOption;
          private_static_ip?: string;
          ip_acl?: string;
        }) => {
          if (dialog === "edit" && !selected) return;
          const name = dialog === "edit" ? selectedName : data.name;
          update({
            internal_load_balancers: [
              ...entries.filter((entry) => entry.name !== name),
              {
                name,
                subnet_id: data.subnet.value,
                private_static_ip: data.private_static_ip || "",
                ip_acl: splitMultilineCommaSeparatedList(data.ip_acl || "")
              }
            ]
          });
        }}
      />
    </>
  );
};
